/**
 * Beta Expired Blocker
 *
 * Full-screen overlay that blocks access when beta period has expired.
 * Prompts user to add payment method to continue using the platform.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Clock,
  CreditCard,
  Lock,
  CheckCircle,
  LogOut,
  Loader2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { api, APIError } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

interface BetaExpiredBlockerProps {
  betaEndDate: string;
}

export const BetaExpiredBlocker: React.FC<BetaExpiredBlockerProps> = ({ betaEndDate }) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const endDate = new Date(betaEndDate);
  const formattedEndDate = endDate.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const handleSetupPayment = async () => {
    setLoading(true);
    try {
      logger.debug('🔄 Redirecting to payment setup...');

      // Call Stripe checkout creation using type-safe API client
      const data = await api.createCheckoutSession({
        tier: 'consultant', // Default tier for beta conversion
      });

      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error: any) {
      if (error instanceof APIError) {
        logger.error('❌ Error creating checkout session:', {
          code: error.code,
          message: error.message,
          status: error.status
        });
        toast.error('Impossible de créer la session de paiement', {
          description: error.message
        });
      } else {
        logger.error('❌ Error creating checkout session:', error);
        toast.error('Impossible de créer la session de paiement', {
          description: error.message
        });
      }
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSignOut}
          className="flex items-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          Se déconnecter
        </Button>
      </div>

      <Card className="max-w-2xl w-full shadow-2xl">
        <CardHeader className="text-center pb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 dark:bg-amber-900/20 rounded-full mb-4 mx-auto">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          <CardTitle className="text-2xl mb-2">
            Votre période Beta est terminée
          </CardTitle>
          <CardDescription className="text-base">
            Votre accès gratuit de 2 semaines s'est terminé le {formattedEndDate}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
            <Lock className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Compte bloqué</strong> — Pour continuer à utiliser Subvention360,
              vous devez configurer votre moyen de paiement.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">
              Continuez avec un abonnement flexible
            </h3>
            <p className="text-sm text-muted-foreground">
              Débloquez votre compte et retrouvez l'accès à toutes les fonctionnalités
              avec notre facturation au volume d'imports.
            </p>

            <div className="grid gap-3 mt-4">
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-blue-900 dark:text-blue-100">
                    Facturation au volume
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    Payez uniquement pour les clients importés chaque mois. À partir de 29€/mois pour 1-10 imports.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-green-900 dark:text-green-100">
                    Aucun engagement
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300">
                    Annulez à tout moment. Aucune période d'engagement minimum requise.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-purple-900 dark:text-purple-100">
                    Vos données sont conservées
                  </div>
                  <div className="text-sm text-purple-700 dark:text-purple-300">
                    Tous vos clients, recherches et données beta sont sauvegardés et prêts à l'emploi.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4 border-t">
            <Button
              onClick={handleSetupPayment}
              disabled={loading}
              size="lg"
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Préparation...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Configurer mon moyen de paiement
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Paiement sécurisé via Stripe • Aucun prélèvement aujourd'hui
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
